import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../react-query/queryKeys";
import {
  Loader,
  Message,
  Modal,
  SelectPicker,
  Uploader,
  useToaster,
} from "rsuite";
import Button from "../button";
import {
  useQuoteUploadMetaData,
  useQuoteUploadSubmit,
  useSpotsContracts,
} from "../../react-query/hooks/HQuoteUpload";
import {
  ICombinedQuoteSubmitPayload,
  IQuoteUploadPayload,
  IQuoteUploadStatusPayload,
} from "../../services/interfaces/IQuoteUpload";
import { FileType, UploaderProps } from "rsuite/esm/Uploader";
import FileUploadIcon from "@rsuite/icons/FileUpload";

const CONTRACT_TYPE_OPTIONS = [
  { label: "Firm/Float", value: "Firm/Float" },
  { label: "Spot", value: "Spot" },
];

const MONTH_SHORT_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const buildMonthOptionsForYear = (year: number) =>
  MONTH_SHORT_NAMES.map((month) => {
    const label = `${month} ${year}`;
    return { label, value: label };
  });

const FULL_MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

const parseNegotiationMonthDate = (negotiationMonth?: string): Date | null => {
  if (!negotiationMonth) return null;

  // Handles formats like "26 May 2026" and standard Date-parsable values.
  const directDate = new Date(negotiationMonth);
  if (!Number.isNaN(directDate.getTime())) {
    return directDate;
  }

  // Handles "May 2026" or "Jan 2026" by assuming day 1.
  const monthYearMatch = negotiationMonth.match(
    /(January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{4})/i,
  );
  if (monthYearMatch) {
    const parsed = new Date(`${monthYearMatch[1]} 1, ${monthYearMatch[2]}`);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  return null;
};

/** Returns [negotiation month, one month before] as SelectPicker options. */
const buildFirmFloatMonthOptions = (
  negotiationMonth?: string,
): Array<{ label: string; value: string }> => {
  const parsedDate = parseNegotiationMonthDate(negotiationMonth);
  if (!parsedDate) return [];

  const monthIndex = parsedDate.getMonth();
  const year = parsedDate.getFullYear();
  const prevMonthIndex = monthIndex === 0 ? 11 : monthIndex - 1;
  const prevYear = monthIndex === 0 ? year - 1 : year;

  const curr = `${MONTH_SHORT_NAMES[monthIndex]} ${year}`;
  const prev = `${MONTH_SHORT_NAMES[prevMonthIndex]} ${prevYear}`;

  return [
    { label: curr, value: curr },
    { label: prev, value: prev },
  ];
};

const getReferenceYear = (negotiationMonth?: string) => {
  if (!negotiationMonth) return new Date().getFullYear();

  const matchedYear = negotiationMonth.match(/\b(19|20)\d{2}\b/);
  if (matchedYear) {
    return Number(matchedYear[0]);
  }

  const parsed = new Date(negotiationMonth);
  return Number.isNaN(parsed.getTime())
    ? new Date().getFullYear()
    : parsed.getFullYear();
};

// Parse a toggle date (yyyy-mm-dd, MM/DD/YYYY, or Date-parseable) into a
// timezone-safe US label and a long value used as the quoteDate.
const SPOT_MONTH_LONG_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const getOrdinalSuffix = (day: number) => {
  const remainder10 = day % 10;
  const remainder100 = day % 100;
  if (remainder100 >= 11 && remainder100 <= 13) {
    return "th";
  }
  if (remainder10 === 1) {
    return "st";
  }
  if (remainder10 === 2) {
    return "nd";
  }
  if (remainder10 === 3) {
    return "rd";
  }
  return "th";
};

const parseSpotWeekDate = (
  dateStr?: string,
  fallbackYear?: string,
): { usLabel: string; longValue: string } | undefined => {
  if (!dateStr) {
    return undefined;
  }

  let year: string | undefined;
  let month: string | undefined;
  let day: string | undefined;

  const isoMatch = dateStr.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  // Toggle dates arrive as DD/MM or DD/MM/YYYY (day first, not Date-parseable).
  const slashMatch = dateStr
    .trim()
    .match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?$/);

  if (isoMatch) {
    [, year, month, day] = isoMatch;
  } else if (slashMatch) {
    day = slashMatch[1].padStart(2, "0");
    month = slashMatch[2].padStart(2, "0");
    const rawYear = slashMatch[3];
    if (rawYear) {
      year = rawYear.length === 2 ? `20${rawYear}` : rawYear;
    } else {
      year = fallbackYear ?? String(new Date().getFullYear());
    }
  } else {
    const parsed = new Date(dateStr);
    if (Number.isNaN(parsed.getTime())) {
      return undefined;
    }
    year = String(parsed.getFullYear());
    month = String(parsed.getMonth() + 1).padStart(2, "0");
    day = String(parsed.getDate()).padStart(2, "0");
  }

  const monthIndex = parseInt(month, 10) - 1;
  if (monthIndex < 0 || monthIndex > 11) {
    return undefined;
  }

  const dayNum = parseInt(day, 10);

  const SPOT_MONTH_SHORT_NAMES = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  return {
    usLabel: `${SPOT_MONTH_SHORT_NAMES[monthIndex]} ${dayNum}${getOrdinalSuffix(dayNum)}`,
    longValue: `${day} ${SPOT_MONTH_LONG_NAMES[monthIndex]} ${year}`,
  };
};

const getContractDate = (
  quoteDate: string | undefined,
  contractType: string | undefined,
): string | undefined => {
  if (!quoteDate || !contractType) return undefined;

  if (contractType === "Spot") {
    // Convert "15 January 2026" to "2026-01-15"
    const date = new Date(quoteDate);
    if (Number.isNaN(date.getTime())) return undefined;
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  } else if (contractType === "Firm/Float") {
    // Convert "Jan 2026" to "2026-01-01"
    const monthYearMatch = quoteDate.match(/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s(\d{4})$/);
    if (!monthYearMatch) return undefined;

    const monthMap: { [key: string]: string } = {
      Jan: "01",
      Feb: "02",
      Mar: "03",
      Apr: "04",
      May: "05",
      Jun: "06",
      Jul: "07",
      Aug: "08",
      Sep: "09",
      Oct: "10",
      Nov: "11",
      Dec: "12",
    };

    const month = monthMap[monthYearMatch[1]];
    const year = monthYearMatch[2];
    return `${year}-${month}-01`;
  }

  return undefined;
};

interface IQuoteUploadModalProps {
  openQuoteModal: boolean;
  setIsMoreInfo: (value: boolean) => void;
  setOpenQuoteModal: (value: boolean) => void;
  setModalType: (value: string) => void;
  negotiationMonth?: string | undefined;
  commodity?: string | undefined;
  quoteUploadStatusPayload?: IQuoteUploadStatusPayload | undefined;
  wireQuotationDate?: string | undefined;
  screenStatus: boolean;
  allowAssessment: boolean;
  role: string;
  setMoreInforMessage: (value: any) => void;
  moreInfoMessage: { heading: string; message: string };
}

const QuoteUploadModal = ({
  setMoreInforMessage,
  setModalType,
  moreInfoMessage,
  setIsMoreInfo,
  openQuoteModal,
  setOpenQuoteModal,
  negotiationMonth,
  commodity,
  quoteUploadStatusPayload,
  wireQuotationDate,
  screenStatus,
  allowAssessment,
  role,
}: IQuoteUploadModalProps) => {
  const toaster = useToaster();
  const queryClient = useQueryClient();
  const [uploadQuotePayload, setUploadQuotePayload] =
    useState<IQuoteUploadPayload>({
      vendor: undefined,
      quoteNumber: undefined,
      quoteDate: negotiationMonth ?? undefined,
      contractType: undefined,
      user: localStorage?.getItem("userEmail") ?? undefined,
      commodity: commodity ?? undefined,
      file: undefined,
    });

  const [filesList, setFilesList] = useState<FileType[]>([]);
  const [validationMessage, setValidationMessage] = useState<string>();
  const [isQuoteUploading, setQuoteUploading] = useState(false);

  const selectedContractType = uploadQuotePayload.contractType;
  const isSpotType = selectedContractType === "Spot";
  const isFirmOrFloatType = selectedContractType === "Firm/Float";

  const referenceYear = getReferenceYear(negotiationMonth);
  const firmFloatDateOptions = buildFirmFloatMonthOptions(negotiationMonth);
  const defaultFirmFloatDate =
    firmFloatDateOptions[0]?.value ?? `Jan ${referenceYear}`;

  const quoteDateOptions = isFirmOrFloatType ? firmFloatDateOptions : [];

  const { spotsData } = useSpotsContracts(
    "current",
    wireQuotationDate,
    commodity,
    commodity === "Copper Wire",
  );
  const toggleDates = spotsData?.toggleDates as
    | { currentDate?: string; previousDate?: string }
    | undefined;
  const spotWeekOptions = [toggleDates?.currentDate, toggleDates?.previousDate]
    .map((date) => parseSpotWeekDate(date, String(referenceYear)))
    .filter((info): info is { usLabel: string; longValue: string } => Boolean(info))
    .map((info) => ({
      label: `Week of ${info.usLabel}`,
      value: info.longValue,
    }));

  const { metaData } = useQuoteUploadMetaData(commodity);
  const {
    mutate,
    uploadSuccess,
    successMessage,
    setUploadSuccess,
    errorMessage,
    setErrorMessage,
  } = useQuoteUploadSubmit();

  useEffect(() => {
    if (commodity) {
      setUploadQuotePayload((prev) => ({
        ...prev,
        commodity: commodity,
      }));
    }
  }, [commodity]);

  useEffect(() => {
    if (negotiationMonth) {
      setUploadQuotePayload((prev) => ({
        ...prev,
        quoteDate: negotiationMonth,
      }));
    }
  }, [negotiationMonth]);

  useEffect(() => {
    if (commodity !== "Copper Wire") return;

    if (!selectedContractType) {
      setUploadQuotePayload((prev) => ({
        ...prev,
        quoteDate: undefined,
      }));
      return;
    }

    if (isFirmOrFloatType) {
      setUploadQuotePayload((prev) => ({
        ...prev,
        quoteDate:
          prev.quoteDate &&
          /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s\d{4}$/.test(prev.quoteDate) &&
          firmFloatDateOptions.some((o) => o.value === prev.quoteDate)
            ? prev.quoteDate
            : defaultFirmFloatDate,
      }));
      return;
    }

    if (isSpotType) {
      setUploadQuotePayload((prev) => ({
        ...prev,
        quoteDate:
          prev.quoteDate &&
          spotWeekOptions.some((o) => o.value === prev.quoteDate)
            ? prev.quoteDate
            : spotWeekOptions[0]?.value,
      }));
    }
  }, [
    selectedContractType,
    isFirmOrFloatType,
    isSpotType,
    negotiationMonth,
    referenceYear,
    spotWeekOptions,
  ]);

  const handleClickMoreInfoModal = (value: any, type: string) => {
    setMoreInforMessage(value);
    setIsMoreInfo(true);
    setModalType(type);
  };

  useEffect(() => {
    if (uploadSuccess && openQuoteModal) {
      setQuoteUploading(false);
      setUploadSuccess(false);
      setOpenQuoteModal(false);
      if (commodity === "Copper Wire") {
        toaster.push(
          <Message showIcon type={"success"} closable>
            <strong>{successMessage && successMessage?.message}</strong>
          </Message>,
          { placement: "topCenter", duration: 5000 },
        );

        {
          successMessage &&
            successMessage.warning &&
            toaster.push(
              <Message showIcon type="warning" closable>
                <strong>
                  {successMessage?.warning?.heading}
                  &nbsp;
                  {successMessage?.warning?.message && (
                    <span
                      onClick={() =>
                        handleClickMoreInfoModal(
                          successMessage?.warning,
                          "warning",
                        )
                      }
                      className="text-blue-600 cursor-pointer underline"
                    >
                      {" "}
                      &nbsp; ...more
                    </span>
                  )}
                </strong>
              </Message>,
              { placement: "topCenter", duration: 10000 },
            );
        }
      } else {
        toaster.push(
          <Message showIcon type={"success"} closable>
            <strong>Quote Upload Successful!</strong>
          </Message>,
          { placement: "topCenter", duration: 5000 },
        );
      }
      queryClient.refetchQueries({
        queryKey: [queryKeys.screenConfig], // refetch the filters to get the updated value for disabling the assessment for that commodity
      });
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
  }, [uploadSuccess]);
  useEffect(() => {
    if (errorMessage && openQuoteModal) {
      setQuoteUploading(false);

      if (commodity === "Copper Wire") {
        toaster.push(
          <Message showIcon type={"error"} closable>
            <strong>
              {errorMessage?.heading}
              &nbsp;
              {errorMessage?.message && (
                <span
                  onClick={() =>
                    handleClickMoreInfoModal(errorMessage, "error")
                  }
                  className=" text-blue-600 cursor-pointer underline"
                >
                  ...more
                </span>
              )}
            </strong>
          </Message>,
          { placement: "topCenter", duration: 5000 },
        );
      } else {
        toaster.push(
          <Message showIcon type={"error"} closable>
            <strong>{errorMessage}</strong>
          </Message>,
          { placement: "topCenter", duration: 5000 },
        );
      }
      setErrorMessage(undefined);
    }
  }, [errorMessage]);

  const handleFieldChange = (
    value: string | null,
    key: keyof IQuoteUploadPayload,
  ) => {
    setUploadQuotePayload({
      ...uploadQuotePayload,
      [`${key}`]: value,
    });
  };

  //Validations messages to be displayed on the modal
  const validationMessages = {
    fileLimit: "Only one file can be uploaded at a time.",
    fileSize: "File size should not exceed 10MB.",
    fileFormat: " Only Excel files (.xlsx, .xls) are allowed.",
  };

  // Allowed file types and max size
  const allowedTypes = [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
  ];
  const maxSize = 10 * 1024 * 1024; // 10MB in bytes

  // Handle file selection
  const handleFileChange: UploaderProps["onChange"] = (files) => {
    if (files.length > 0) {
      const selectedFile = files[0].blobFile as File;

      if (!allowedTypes.includes(selectedFile.type)) {
        setFilesList([]);
        setUploadQuotePayload({
          ...uploadQuotePayload,
          file: undefined,
        });
        return;
      }

      if (selectedFile.size > maxSize) {
        setFilesList([]);
        setUploadQuotePayload({
          ...uploadQuotePayload,
          file: undefined,
        });
        return;
      }

      setFilesList(files);
      setUploadQuotePayload({
        ...uploadQuotePayload,
        file: selectedFile,
      });
    } else {
      setFilesList([]);
      setUploadQuotePayload({
        ...uploadQuotePayload,
        file: undefined,
      });
    }
  };

  const handleFileCheck: UploaderProps["shouldQueueUpdate"] = (
    files,
    newFile,
  ) => {
    // Normalize "files" so that we always work with an array.
    const fileArray: FileType[] = Array.isArray(files) ? files : [files];

    // If more than one file is present, stop the update.
    if (fileArray.length > 1) {
      toaster.push(
        <Message type="error">
          Only one file can be uploaded at a time.
        </Message>,
        { placement: "topCenter" },
      );
      setValidationMessage(validationMessages.fileLimit);
      return false;
    }

    // At this point, fileArray.length === 1.
    const selectedFile = fileArray[0].blobFile as File;

    // Validate the file type.
    if (!allowedTypes.includes(selectedFile.type)) {
      toaster.push(
        <Message type="error">
          Only Excel files (.xlsx, .xls) are allowed.
        </Message>,
        { placement: "topCenter" },
      );
      setValidationMessage(validationMessages.fileFormat);
      return false;
    }

    // Validate the file size.
    if (selectedFile.size > maxSize) {
      toaster.push(
        <Message type="error">File size should not exceed 10MB.</Message>,
        { placement: "topCenter" },
      );
      setValidationMessage(validationMessages.fileSize);
      return false;
    }

    setValidationMessage(undefined);
    // Update state if everything is valid.
    return true;
  };

  const submitQuote = () => {
    if (commodity !== "Copper Wire") {
      if (
        !uploadQuotePayload.file ||
        !uploadQuotePayload.vendor ||
        !uploadQuotePayload.quoteNumber ||
        !uploadQuotePayload.quoteDate ||
        !uploadQuotePayload.commodity ||
        !uploadQuotePayload.user ||
        !quoteUploadStatusPayload
      )
        return;

      const contractDate = getContractDate(
        uploadQuotePayload.quoteDate,
        uploadQuotePayload.contractType,
      );

      const combinedPayload: ICombinedQuoteSubmitPayload = {
        payload: {
          ...uploadQuotePayload,
          contractDate,
        },
        refetchPayload: quoteUploadStatusPayload,
      };

      setQuoteUploading(true);
      mutate(combinedPayload);
    } else {
      if (
        !uploadQuotePayload.file ||
        !uploadQuotePayload.commodity ||
        !uploadQuotePayload.user ||
        !quoteUploadStatusPayload
      )
        return;

      const contractDate = getContractDate(
        uploadQuotePayload.quoteDate,
        uploadQuotePayload.contractType,
      );

      const apiContractType =
        uploadQuotePayload.contractType === "Firm/Float"
          ? "[Firm,Float]"
          : uploadQuotePayload.contractType;

      const combinedPayload: ICombinedQuoteSubmitPayload = {
        payload: {
          ...uploadQuotePayload,
          contractType: apiContractType,
          contractDate,
        },
        refetchPayload: quoteUploadStatusPayload,
      };

      setQuoteUploading(true);
      mutate(combinedPayload);
    }
  };

  return (
    <div className="flex justify-center items-center">
      <Modal
        className="w-[30vw] quote-upload-modal"
        open={openQuoteModal}
        onClose={() => {
          !isQuoteUploading && setOpenQuoteModal(false);
        }}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header>
          <Modal.Title className="font-bold text-[1.05vw]">
            {"Quote Upload"}
          </Modal.Title>
        </Modal.Header>

        {isQuoteUploading ? (
          <Modal.Body style={{ minHeight: "42.5vh" }}>
            <Loader
              backdrop
              content="Loading..."
              size="md"
              vertical
              className="text-black font-bold"
              style={{ opacity: 0.6, minHeight: "30vh" }}
            />
          </Modal.Body>
        ) : (
          <>
            <Modal.Body>
              <div className="flex flex-col justify-between items-center px-[1vh] pt-[0vh] pb-[2vh]">
                {negotiationMonth && negotiationMonth?.length !== 0 && (
                  <div className="w-full pb-[1.5vh]">
                    <div className="flex justify-start items-center w-full pb-[1.25vh]">
                      <span className="text-[0.9vw] font-semibold">
                        Upload the quote for:
                      </span>
                      <span className="text-[0.9vw] font-semibold pl-[0.75vh] underline text-deloitteGreen">
                        {negotiationMonth}
                      </span>
                    </div>
                    {commodity === "Copper Wire" && (
                      <div className="flex gap-[1vw] w-full">
                        <div className="flex flex-col justify-start items-start w-full basis-[50%]">
                          <label className="font-bold text-fs16">Contract Type</label>
                          <SelectPicker
                            className="dropdown-responsive-width"
                            data={CONTRACT_TYPE_OPTIONS}
                            labelKey="label"
                            valueKey="value"
                            style={{ width: "100%" }}
                            onChange={(value: string | null) =>
                              handleFieldChange(value, "contractType")
                            }
                            placeholder="Select"
                            cleanable={false}
                            searchable={false}
                            value={uploadQuotePayload?.contractType}
                          />
                        </div>
                        <div className="flex flex-col justify-start items-start w-full basis-[50%]">
                          <label className="font-bold text-fs16">Date</label>
                          {isSpotType ? (
                            <SelectPicker
                              className="dropdown-responsive-width"
                              data={spotWeekOptions}
                              labelKey="label"
                              valueKey="value"
                              style={{ width: "100%" }}
                              onChange={(value: string | null) =>
                                handleFieldChange(value, "quoteDate")
                              }
                              placeholder="Select"
                              cleanable={false}
                              searchable={false}
                              value={uploadQuotePayload?.quoteDate}
                            />
                          ) : isFirmOrFloatType ? (
                            <SelectPicker
                              className="dropdown-responsive-width"
                              data={quoteDateOptions}
                              labelKey="label"
                              valueKey="value"
                              style={{ width: "100%" }}
                              onChange={(value: string | null) =>
                                handleFieldChange(value, "quoteDate")
                              }
                              placeholder="Select"
                              cleanable={false}
                              value={uploadQuotePayload?.quoteDate}
                            />
                          ) : (
                            <SelectPicker
                              className="dropdown-responsive-width"
                              data={[]}
                              labelKey="label"
                              valueKey="value"
                              style={{ width: "100%" }}
                              placeholder="Select Contract Type First"
                              cleanable={false}
                              disabled
                              value={null}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {commodity !== "Copper Wire" && (
                  <div className="flex w-full gap-[2vw]">
                    <div className="flex flex-col justify-start items-start w-full basis-[50%]">
                      <label className="font-bold text-fs16">Supplier</label>
                      <SelectPicker
                        className="dropdown-responsive-width"
                        data={metaData?.vendors ?? []}
                        labelKey="vendor"
                        valueKey="vendor"
                        style={{ width: "100%" }}
                        onChange={(value: string | null, e: any) =>
                          handleFieldChange(value, "vendor")
                        }
                        placeholder="Select"
                        cleanable={false}
                        value={uploadQuotePayload?.vendor}
                      ></SelectPicker>
                    </div>
                    <div className="flex flex-col justify-start items-start w-full basis-[50%]">
                      <label className="font-bold text-fs16">Quote</label>
                      <SelectPicker
                        className="dropdown-responsive-width"
                        data={metaData?.quotes ?? []}
                        disabledItemValues={
                          metaData && metaData?.quotes
                            ? [
                              ...metaData?.quotes
                                .filter((ele) => {
                                  if (!screenStatus)
                                    return Number(ele.quoteNumber) != 10;
                                  else {
                                    if (allowAssessment) {
                                      return ele.quoteNumber === "10";
                                    } else {
                                      if (role === "Lead") {
                                        return false;
                                      } else {
                                        return Number(ele.quoteNumber) != 10;
                                      }
                                    }
                                    // if (!allowAssessment && role !== "Lead") {
                                    //   return true;
                                    // } else {
                                    //   return ele.quoteNumber === "10";
                                    // }
                                  }
                                })
                                .map((ele) => ele.quoteNumber),
                            ]
                            : []
                        }
                        labelKey="quoteName"
                        valueKey="quoteNumber"
                        style={{ width: "100%" }}
                        onChange={(value: string | null, e: any) =>
                          handleFieldChange(value, "quoteNumber")
                        }
                        placeholder="Select"
                        cleanable={false}
                        value={uploadQuotePayload?.quoteNumber}
                      ></SelectPicker>
                    </div>
                  </div>
                )}
              </div>
              <div className="px-[1vh]">
                <Uploader
                  autoUpload={false}
                  fileList={filesList}
                  action="#"
                  draggable
                  shouldQueueUpdate={handleFileCheck}
                  onChange={handleFileChange}
                  accept=".xlsx,.xls"
                  multiple={false}
                >
                  <div
                    style={{
                      height: "12vw",
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FileUploadIcon
                      style={{ fontSize: "5vw", color: "#86BC25" }}
                    />
                    <span className="text-fs16 mt-[2vh]">
                      Click or Drag files to this area to upload
                    </span>
                  </div>
                </Uploader>
              </div>
              {validationMessage && (
                <div className="text-[0.75vw] font-semibold text-red-700 w-full flex justify-start px-[1vh]">
                  *{validationMessage}
                </div>
              )}
              <div className="flex justify-center pt-[1vh]">
                <Button
                  className="w-[5vw]"
                  label={"Submit"}
                  bgColor={"#86BC25"}
                  onClick={submitQuote}
                  disabled={
                    !uploadQuotePayload.file ||
                    (commodity !== "Copper Wire" &&
                      (!uploadQuotePayload.vendor ||
                        !uploadQuotePayload.quoteNumber ||
                        !uploadQuotePayload.quoteDate)) ||
                    !uploadQuotePayload.commodity ||
                    !uploadQuotePayload.user
                  }
                />
              </div>
            </Modal.Body>
            <Modal.Footer>
              <div className="flex justify-center text-fs14">
                Upload an excel(.xlsx, .xls) with size less than 10 MB
              </div>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </div>
  );
};

export default QuoteUploadModal;
